// Foundation's Gregorian `Calendar` in a time zone, over epoch milliseconds.

export const referenceDate = Date.UTC(2001, 0, 1);
const dayMs = 86_400_000;

/** `Date.timeIntervalSinceReferenceDate`, as the default `JSONEncoder` writes a date. */
export function wireDate(ms: number): number {
  return (ms - referenceDate) / 1000;
}

export interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
  /** 1 is Sunday, as Foundation counts. */
  weekday: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let found = formatters.get(timeZone);
  if (found === undefined) {
    found = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
    formatters.set(timeZone, found);
  }
  return found;
}

export function parts(ms: number, timeZone: string): Parts {
  const values: Record<string, number> = {};
  for (const part of formatter(timeZone).formatToParts(new Date(ms))) {
    if (part.type !== "literal") values[part.type] = Number(part.value);
  }
  const year = values.year!, month = values.month!, day = values.day!;
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 1;
  return {
    year,
    month,
    day,
    hour: values.hour! % 24,
    minute: values.minute!,
    second: values.second!,
    millisecond: ((ms % 1000) + 1000) % 1000,
    weekday,
  };
}

function offset(ms: number, timeZone: string): number {
  const p = parts(ms, timeZone);
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, p.millisecond);
  return asUTC - ms;
}

/** `calendar.date(from:)`: a wall-clock time in the zone, overflowing days and months as Foundation does. */
export function fromParts(
  timeZone: string,
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0,
): number {
  const wall = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  let guess = wall - offset(wall, timeZone);
  const corrected = wall - offset(guess, timeZone);
  if (corrected !== guess) guess = corrected;
  return guess;
}

export function startOfDay(ms: number, timeZone: string): number {
  const p = parts(ms, timeZone);
  return fromParts(timeZone, p.year, p.month, p.day);
}

/** `calendar.date(byAdding: .day, value:, to:)`: the same wall-clock time, days on. */
export function addDays(ms: number, days: number, timeZone: string): number {
  const p = parts(ms, timeZone);
  return fromParts(timeZone, p.year, p.month, p.day + days, p.hour, p.minute, p.second, p.millisecond);
}



/** `PublishedWeeks.day`: `YYYY-MM-DD` of the day `ms` falls on in the zone. */
export function dayString(ms: number, timeZone: string): string {
  const p = parts(ms, timeZone);
  return `${pad(p.year, 4)}-${pad(p.month, 2)}-${pad(p.day, 2)}`;
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, "0");
}

/** `PublishedWeeks.date(from:)`: the start of that day in the zone. */
export function dateFromDay(text: string, timeZone: string): number | null {
  const pieces = text.split("-").filter((piece) => piece !== "").map((piece) => (/^[0-9]+$/.test(piece) ? Number(piece) : NaN));
  if (pieces.length !== 3 || pieces.some((piece) => Number.isNaN(piece))) return null;
  return fromParts(timeZone, pieces[0]!, pieces[1]!, pieces[2]!);
}

/** `PublishedWeeks.monday(of:)`: weeks start on Monday, as the coach's do. */
export function mondayOf(text: string): string | null {
  const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(text);
  if (match === null) return null;
  const year = Number(match[1]), month = Number(match[2]), date = Number(match[3]);
  const ms = Date.UTC(year, month - 1, date);
  const check = new Date(ms);
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== date) return null;
  const weekday = check.getUTCDay() + 1;
  const back = (weekday + 5) % 7;
  return dayString(ms - back * dayMs, "UTC");
}
